import { useState } from "react";
import { heroStats, hotKeywords } from "@/mocks/home";

interface HeroProps {
  onNotify: (message: string) => void;
}

export default function Hero({ onNotify }: HeroProps) {
  const [keyword, setKeyword] = useState("");
  const [slot, setSlot] = useState("午夜场");
  const slots = ["午夜场", "最新电影", "无删减版"];

  const handleSearch = () => {
    const q = keyword.trim();
    onNotify(q ? `已按「${q}」为你筛选深夜片单` : `已按「${slot}」为你整理今晚的片单`);
  };

  return (
    <section id="top" className="relative w-full overflow-hidden border-b border-background-300/40">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=stylized%20abstract%20cinematic%20night%20scene%20with%20warm%20amber%20stage%20light%20beams%20cutting%20through%20deep%20charcoal%20darkness%2C%20soft%20bokeh%20dust%20particles%2C%20subtle%20film%20grain%2C%20dramatic%20high%20contrast%2C%20moody%20atmospheric%20minimal%20composition&width=1920&height=1080&seq=midnight-hero-bg-01&orientation=landscape"
          alt="午夜影院深夜观影氛围"
          className="h-full w-full object-cover object-top opacity-70"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/80 to-background-50" />
      <div className="absolute inset-0 shell-grid opacity-30" />

      <div className="relative z-10 mx-auto flex w-full max-w-[1180px] flex-col items-center px-4 py-16 text-center md:px-6 md:py-24">
        <span className="inline-flex items-center gap-2 rounded-full border border-primary-500/40 bg-primary-500/10 px-4 py-1.5 text-xs text-primary-200">
          <i className="ri-moon-clear-line" />
          深夜不打烊 · 在线观看
        </span>

        <h1 className="font-display mt-6 text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
          深夜打开午夜影院
          <br className="hidden md:block" />
          <span className="text-primary-300"> 一个人也能安心刷到尽兴</span>
        </h1>

        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-500 md:text-base">
          在线观看最新电影，午夜场与无删减版资源集中收录。悬疑、剧情、经典老片，按深夜该有的氛围慢慢挑，
          不用在长长的列表里翻来覆去。
        </p>

        <div className="mt-8 w-full max-w-3xl rounded-xl border border-background-300/60 bg-background-100/90 p-2 shadow-sm backdrop-blur-sm md:rounded-full">
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <div className="flex flex-1 items-center gap-2 rounded-lg px-3 py-2 md:rounded-full">
              <i className="ri-search-line text-base text-foreground-500" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
                placeholder="搜片名、导演或一句话关键词"
                className="w-full border-0 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="button"
              onClick={handleSearch}
              className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:rounded-full"
            >
              <i className="ri-search-eye-line" />
              开始找片
            </button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          <span className="text-xs text-foreground-500">今晚上热门：</span>
          {slots.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSlot(s)}
              className={
                s === slot
                  ? "cursor-pointer whitespace-nowrap rounded-full border border-primary-500/50 bg-primary-500/15 px-3 py-1 text-xs text-primary-200"
                  : "cursor-pointer whitespace-nowrap rounded-full border border-background-300/60 px-3 py-1 text-xs text-foreground-500 transition-colors hover:text-foreground-900"
              }
            >
              {s}
            </button>
          ))}
          {hotKeywords.slice(3, 6).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => onNotify(`已为你打开「${k}」相关片单`)}
              className="cursor-pointer whitespace-nowrap rounded-full border border-background-300/60 px-3 py-1 text-xs text-foreground-500 transition-colors hover:border-primary-400 hover:text-primary-300"
            >
              {k}
            </button>
          ))}
        </div>

        <div className="mt-12 grid w-full max-w-3xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-background-300/50 bg-background-300/40 md:grid-cols-4">
          {heroStats.map((stat) => (
            <div key={stat.id} className="flex flex-col items-center bg-background-100 px-4 py-5">
              <span className="font-label text-2xl text-primary-300 md:text-3xl">{stat.value}</span>
              <span className="mt-1 text-xs text-foreground-500">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}